import {debounce, fetchCart} from 'theme';

// Persisted across pages/reloads so the customer's note is never lost even while the addon is unchecked
const NOTE_STORAGE_KEY = 'theme:gift-wrapping-addon-note';

// Keeps typing feeling responsive (session storage writes + cross-instance sync)
const NOTE_PERSIST_DEBOUNCE_MS = 200;
// Long enough to only fire once typing has actually stopped, so we don't spam the cart API on every keystroke
const NOTE_CART_SYNC_DEBOUNCE_MS = 1500;

// Shared across every instance on the page, so a quantity bump isn't corrected twice in parallel
const pendingQuantityClamps = new Set();

/**
 * Controls the gift wrapping addon checkbox (and optional gift note textarea) rendered on the product page and in
 * the cart drawer footer. Checking the box adds the configured addon product to the cart (immediately in the cart
 * drawer, or once the main product is added on the product page); unchecking it removes the addon from the cart.
 */
class GiftWrappingAddon extends HTMLElement {
  #abortController;
  #checkbox;
  #noteContainer;
  #noteField;
  #productId;
  #variantId;
  #noteProperty;
  #context;
  #mainProductId;
  #inCart;
  #persistNoteDebounced;
  #syncNoteWithCartDebounced;

  connectedCallback() {
    this.#checkbox = this.querySelector('input[type="checkbox"]');

    if (!this.#checkbox) return;

    this.#abortController = new AbortController();

    const signal = this.#abortController.signal;

    this.#noteContainer = this.querySelector('.gift-wrapping-addon__note');
    this.#noteField = this.#noteContainer?.querySelector('textarea');
    this.#productId = Number(this.getAttribute('data-product-id'));
    this.#variantId = Number(this.getAttribute('data-variant-id'));
    this.#noteProperty = this.getAttribute('data-note-property');
    this.#context = this.getAttribute('context');
    this.#mainProductId = Number(this.getAttribute('data-main-product-id'));
    this.#inCart = this.#checkbox.checked;
    this.#persistNoteDebounced = debounce(this.#persistNote.bind(this), NOTE_PERSIST_DEBOUNCE_MS);
    this.#syncNoteWithCartDebounced = debounce(this.#syncNoteWithCart.bind(this), NOTE_CART_SYNC_DEBOUNCE_MS);

    this.#restoreNote();

    this.#checkbox.addEventListener('change', this.#onCheckboxChange.bind(this), {signal});
    this.#noteField?.addEventListener('input', this.#onNoteInput.bind(this), {signal});
    document.addEventListener('cart:change', this.#onCartChange.bind(this), {signal});
    document.addEventListener('gift-wrapping-addon:note-change', this.#onNoteChangeElsewhere.bind(this), {signal});

    if (this.#context === 'product') {
      document.addEventListener('variant:add', this.#onVariantAdd.bind(this), {signal});
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * Session storage always wins on load, since it reflects the customer's most recent edit across any page. If
   * nothing was ever stored, we seed it with whatever note is already attached to the cart line (if any)
   */
  #restoreNote() {
    if (!this.#noteField) return;

    const storedNote = window.sessionStorage.getItem(NOTE_STORAGE_KEY);

    if (storedNote !== null) {
      this.#noteField.value = storedNote;
    } else if (this.#noteField.value !== '') {
      window.sessionStorage.setItem(NOTE_STORAGE_KEY, this.#noteField.value);
    }
  }

  #toggleNoteVisibility() {
    this.#noteContainer?.toggleAttribute('hidden', !this.#checkbox.checked);
  }

  #onNoteInput() {
    this.#persistNoteDebounced();
    this.#syncNoteWithCartDebounced();
  }

  #persistNote() {
    const value = this.#noteField.value;

    // An empty value must clear storage entirely, so a fresh page load reverts to the default (blank) state
    if (value === '') {
      window.sessionStorage.removeItem(NOTE_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(NOTE_STORAGE_KEY, value);
    }

    // Keep any other instance of this component rendered on the same page (product page + cart drawer) in sync
    document.dispatchEvent(new CustomEvent('gift-wrapping-addon:note-change', {detail: {value}}));
  }

  /**
   * If the addon is already in the cart (regardless of which instance/context this is), push the latest note text
   * to the line item's properties so it actually reaches the order
   */
  async #syncNoteWithCart() {
    if (!this.#inCart) return;

    const cart = await fetchCart;
    const lineItem = cart.items.find((item) => item.product_id === this.#productId);

    if (!lineItem) return;

    const note = this.#noteField.value.trim();
    const properties = {...lineItem.properties};

    // Shopify's cart/change.js only clears a property when it's explicitly sent as an empty string, not when omitted
    properties[this.#noteProperty] = note;

    const updatedCart = await this.#changeCart({id: lineItem.key, quantity: lineItem.quantity, properties}, {busy: false});

    if (!updatedCart) return;

    this.#dispatchCartChange(updatedCart, 'gift-wrapping-addon:update-note');
  }

  #onNoteChangeElsewhere(event) {
    if (this.#noteField && this.#noteField !== document.activeElement) {
      this.#noteField.value = event.detail.value;
    }
  }

  async #onCheckboxChange() {
    this.#toggleNoteVisibility();

    if (this.#checkbox.checked) {
      if (this.#context === 'cart') {
        await this.#addToCart();
      }
      // On the product page, the addon is only added once the main product form is submitted, see #onVariantAdd
    } else {
      await this.#removeFromCartIfPresent();
    }
  }

  /**
   * Once the main product on this page has been added to the cart, add the gift wrapping addon if the customer has
   * checked the box and it isn't already in the cart
   */
  #onVariantAdd(event) {
    const wasMainProductAdded = event.detail.items.some((item) => item.product_id === this.#mainProductId);

    if (!wasMainProductAdded || !this.#checkbox.checked) return;

    const alreadyInCart = event.detail.cart.items.some((item) => item.product_id === this.#productId);

    if (alreadyInCart) return;

    this.#addToCart();
  }

  /**
   * Keep the checkbox in sync whenever the cart changes for any reason (for instance, removing the addon line item
   * directly from the cart UI, or another instance of this component adding/removing it)
   */
  #onCartChange(event) {
    const lineItem = event.detail.cart.items.find((item) => item.product_id === this.#productId);
    const isPresent = Boolean(lineItem);

    if (isPresent !== this.#inCart) {
      this.#inCart = isPresent;
      this.#checkbox.checked = isPresent;
      this.#toggleNoteVisibility();
    }

    // The addon is a toggle, not a quantity-based product; anything above 1 (cart page, quantity selector...) gets clamped back down
    if (lineItem && lineItem.quantity > 1) {
      this.#clampQuantity(lineItem);
    }
  }

  async #clampQuantity(lineItem) {
    if (pendingQuantityClamps.has(this.#productId)) return;

    pendingQuantityClamps.add(this.#productId);

    const updatedCart = await this.#changeCart({id: lineItem.key, quantity: 1}, {busy: false});

    pendingQuantityClamps.delete(this.#productId);

    if (!updatedCart) return;

    this.#dispatchCartChange(updatedCart, 'gift-wrapping-addon:clamp-quantity');
  }

  async #addToCart() {
    const properties = {};
    const note = this.#noteField?.value.trim();

    if (note) {
      properties[this.#noteProperty] = note;
    }

    const sections = this.#collectBundledSections();

    this.#setBusy(true);
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', {bubbles: true}));

    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
      body: JSON.stringify({items: [{id: this.#variantId, quantity: 1, properties}], sections})
    });

    const responseJson = await response.json();

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', {bubbles: true}));
    this.#setBusy(false);

    if (!response.ok) {
      await this.#handleError(responseJson.message);
      return;
    }

    const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    cart.sections = responseJson.sections;

    this.#inCart = true;
    this.#dispatchCartChange(cart, 'gift-wrapping-addon:add');
  }

  async #removeFromCartIfPresent() {
    const cart = await fetchCart;
    const lineItem = cart.items.find((item) => item.product_id === this.#productId);

    if (!lineItem) return;

    const updatedCart = await this.#changeCart({id: lineItem.key, quantity: 0});

    if (!updatedCart) return;

    this.#inCart = false;
    this.#dispatchCartChange(updatedCart, 'gift-wrapping-addon:remove');
  }

  /**
   * Shared helper for any /cart/change.js call. Returns the updated cart, or null if the request failed (in which
   * case #handleError has already resynced the UI with the server truth)
   */
  async #changeCart(payload, {busy = true} = {}) {
    const sections = this.#collectBundledSections();

    if (busy) {
      this.#setBusy(true);
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', {bubbles: true}));
    }

    const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({...payload, sections})
    });

    const cart = await response.json();

    if (busy) {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', {bubbles: true}));
      this.#setBusy(false);
    }

    if (!response.ok) {
      await this.#handleError(cart.message);
      return null;
    }

    return cart;
  }

  /**
   * On failure, resync with the actual server state rather than trusting optimistic UI
   */
  async #handleError(message) {
    const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();

    this.#inCart = cart.items.some((item) => item.product_id === this.#productId);
    this.#checkbox.checked = this.#inCart;
    this.#toggleNoteVisibility();

    document.dispatchEvent(new CustomEvent('toast:show', {detail: {message, tone: 'error'}}));
  }

  #collectBundledSections() {
    const sectionsToBundle = new Set();
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {bubbles: true, detail: {sections: sectionsToBundle}}));
    return [...sectionsToBundle].join(',');
  }

  #dispatchCartChange(cart, baseEvent) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {bubbles: true, detail: {baseEvent, cart}}));
  }

  #setBusy(busy) {
    this.#checkbox.disabled = busy;

    if (busy) {
      this.#checkbox.setAttribute('aria-busy', 'true');
    } else {
      this.#checkbox.removeAttribute('aria-busy');
    }
  }
}

if (!window.customElements.get('gift-wrapping-addon')) {
  window.customElements.define('gift-wrapping-addon', GiftWrappingAddon);
}
